import { OptionInput } from "@/components/Inputs/OptionInput";
import { parseDateString, parseToUTCDate } from "@/lib/utils/parser";
import { useMemo } from "react";

/**
 * When a DATE field is constrained to a small inclusive range (min/max within
 * `DATE_DROPDOWN_MAX_DAYS`), entering a full calendar is overkill — a dropdown
 * of the allowed days is faster and impossible to get wrong. This is a thin
 * wrapper over `OptionInput` (closed-list select) that enumerates the allowed
 * days as options. For larger ranges, callers keep the calendar instead.
 */
export const DATE_DROPDOWN_MAX_DAYS = 14;

/** Inclusive whole-day span between two values, or null if either is unset. */
export function daySpan(
  min: string | Date | undefined,
  max: string | Date | undefined,
): number | null {
  const lo = toUtcMidnight(min);
  const hi = toUtcMidnight(max);
  if (!lo || !hi) return null;
  const ms = hi.getTime() - lo.getTime();
  if (ms < 0) return null;
  return Math.round(ms / 86_400_000) + 1;
}

/**
 * Coerce a bound (ISO-Z, "YYYY-MM-DD", or Date) to a Date. Anything else — e.g.
 * a stray numeric bound from a non-date field — yields undefined instead of
 * throwing on `.toISOString()`. Returns the parsed instant as-is (not floored).
 */
export function boundToDate(value: string | Date | undefined): Date | undefined {
  return value instanceof Date
    ? value
    : parseToUTCDate(typeof value === "string" ? value : undefined);
}

/** Normalise any DATE value (ISO-Z, "YYYY-MM-DD", Date) to a UTC-midnight Date. */
function toUtcMidnight(value: string | Date | undefined): Date | undefined {
  const d = boundToDate(value);
  if (!d) return undefined;
  return new Date(
    Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), 0, 0, 0, 0),
  );
}

const isoOf = (d: Date) => d.toISOString(); // "YYYY-MM-DDT00:00:00.000Z"

type DateRangeDropdownProps = {
  value: string;
  minDate: string | Date;
  maxDate: string | Date;
  onChangeValue: (value: string) => void;
  /** IANA tz used only for the human label (matches TableCellContentDate). */
  timeZone?: string;
  required?: boolean;
  disabled?: boolean;
  error?: string | null;
  border?: string;
  height?: string;
  fontSize?: string;
  padding?: string;
  rounded?: string;
  surface?: string;
};

export const DateRangeDropdown: React.FC<DateRangeDropdownProps> = ({
  value,
  minDate,
  maxDate,
  onChangeValue,
  timeZone,
  required = false,
  disabled = false,
  error,
  border,
  height,
  fontSize,
  padding,
  rounded,
  surface,
}) => {
  const { options, optionLabels, normalizedValue } = useMemo(() => {
    const lo = toUtcMidnight(minDate);
    const hi = toUtcMidnight(maxDate);
    const current = toUtcMidnight(value);

    // Collect every allowed day (UTC-midnight ISO), plus the current value when
    // it falls outside the bounds — otherwise OptionInput's closed list would
    // silently snap the out-of-range value back. Validation still flags it.
    const isoSet = new Set<string>();
    if (lo && hi) {
      for (
        let d = new Date(lo);
        d.getTime() <= hi.getTime();
        d = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() + 1))
      ) {
        isoSet.add(isoOf(d));
      }
    }
    const normalizedValue = current ? isoOf(current) : "";
    if (normalizedValue) isoSet.add(normalizedValue);

    // ISO strings share one fixed format, so lexical compare is chronological.
    const loIso = lo ? isoOf(lo) : null;
    const hiIso = hi ? isoOf(hi) : null;
    const outOfRange = (iso: string) =>
      (loIso !== null && iso < loIso) || (hiIso !== null && iso > hiIso);

    const opts = Array.from(isoSet).sort();
    const labels: Record<string, string> = {};
    for (const iso of opts) {
      const label = parseDateString(parseToUTCDate(iso), "de-DE", timeZone);
      // A value left over from a since-narrowed range stays selectable so it is
      // not silently lost, but is flagged so it doesn't read as a valid day.
      // Language-neutral glyph → no per-locale string needed.
      labels[iso] = outOfRange(iso) ? `⚠ ${label}` : label;
    }
    return { options: opts, optionLabels: labels, normalizedValue };
  }, [minDate, maxDate, value, timeZone]);

  return (
    <OptionInput
      options={options}
      optionLabels={optionLabels}
      defaultValue={normalizedValue}
      required={required}
      disabled={disabled}
      error={error ?? undefined}
      setParent={(iso) => onChangeValue(iso)}
      commitOnSelectOnly={true}
      strictOption={true}
      {...(border ? { border } : {})}
      {...(height ? { height } : {})}
      {...(fontSize ? { fontSize } : {})}
      {...(padding ? { padding } : {})}
      {...(rounded ? { rounded } : {})}
      {...(surface ? { surface } : {})}
    />
  );
};
